import {
  CHART_FOLD_KEYS,
  CalcColumnDefinition,
  CalciteType,
  CalcsDefinition,
  CellId,
  ColumnFilter,
  DisplayTableColumnId,
  ExploreChannel,
  ExploreChartConfig,
  ExploreDetailField,
  ExploreDropSection,
  ExploreField,
  ExploreFieldId,
  ExploreInputField,
  ExploreSpec,
  ExploreSpecUpdateActions,
  FilledDynamicValueTableColumnType,
  HexVersionAtomicOperation,
  HqlAggregationFunction,
  LumpColumnPredicate,
  PivotExploreChannel,
  SemanticAwareColumn,
  SemanticAwareFieldGroup,
  SemanticDatasetName,
  SqlDialect,
  UPDATE_DISPLAY_TABLE_CONFIG,
  assertNever,
  calciteTypeToColumnType,
  columnTypeToCalciteType,
  exploreSpecActions,
  generateColumnIdForField,
  getAllNodesOfType,
  isCalcErrorFatal,
  isPie,
  notNullish,
  parseCalcExpression,
  sqlGenSupportsAggForDialect,
  typedObjectEntries,
  typedObjectKeys,
  validateCalcDefinition,
  zGuard,
} from "@hex/common";
import { capitalize } from "lodash";

import { DisplayTableConfigFragment } from "../hex-version-multiplayer/HexVersionMPModel.generated.js";
import { useCellGetter } from "../hex-version-multiplayer/state-hooks/cellStateHooks.js";
import { useComputedValue } from "../hooks/useComputedValue.js";

export const getSemanticAwareFieldKey = (
  field: Partial<SemanticAwareColumn>,
): string => {
  const key = `${[...(field.queryPath ?? []), field.columnId].join(".")}`;
  if (field.columnAggregation) return ` ${field.columnAggregation}(${key})`;
  else return key;
};

/**
 * Ordered mapping of explore sections to their display labels.
 * Order is used for displaying diffs.
 */
const LabelBySection: Record<ExploreDropSection["type"], string> = {
  "cross-axis": "Y-axis", // Y-axis or X-axis depending on chart orientation
  "base-axis": "X-axis", // Y-axis or X-axis depending on chart orientation
  value: "value",
  color: "color by",
  opacity: "opacity",
  "h-facet": "horizontal split",
  "v-facet": "vertical split",
  tooltip: "tooltip",
  row: "row",
  column: "column",
  groupby: "group by",
  aggregation: "aggregation",
  source: "source",
  destination: "destination",
} as const;

export const compareExploreSection = (
  a: ExploreDropSection["type"],
  b: ExploreDropSection["type"],
) => {
  const keys = typedObjectKeys(LabelBySection);
  return keys.indexOf(a) - keys.indexOf(b);
};

export function getLabelForChannel(
  channel: ExploreDropSection["type"],
  chartConfig?: ExploreChartConfig,
): string {
  if (chartConfig != null) {
    if (channel === "base-axis" && chartConfig.orientation === "horizontal") {
      return "Y-axis";
    }
    if (channel === "cross-axis" && isPie(chartConfig)) {
      return "Size";
    }
    if (channel === "cross-axis" && chartConfig.orientation === "horizontal") {
      return "X-axis";
    }
  }
  return LabelBySection[channel];
}

export const getLabelForExploreChannel = ({
  capitalizeLabel = true,
  channel,
  chartConfig,
  pluralize,
}: {
  channel: ExploreDropSection["type"];
  chartConfig?: ExploreChartConfig;
  pluralize: boolean;
  capitalizeLabel?: boolean;
}): string => {
  const isPivotChannel = zGuard(PivotExploreChannel, channel);
  const label = capitalizeLabel
    ? capitalize(getLabelForChannel(channel, chartConfig))
    : getLabelForChannel(channel, chartConfig);
  return pluralize || isPivotChannel
    ? `${label.replace("axis", "axe")}s`
    : label;
};

export const EXPLORE_CHART_ONLY_AGGREGATIONS: Set<HqlAggregationFunction> =
  new Set(["StdDev", "StdDevPop", "Variance", "VariancePop"]);

export const EXPLORE_PIE_ALLOWED_AGGREGATIONS: Set<HqlAggregationFunction> =
  new Set(["Count", "CountDistinct", "Sum"]);

export const EXPLORE_SANKEY_ALLOWED_AGGREGATIONS: Set<HqlAggregationFunction> =
  new Set(["Count", "CountDistinct", "Sum"]);

export type ExploreFieldOutOfSync = {
  fieldId: string;
  error: ExploreFieldOutOfSyncError;
  usedInDetails?: boolean;
  channel?: ExploreChannel;
  usedInFilters?: boolean;
  calcColumnNames: DisplayTableColumnId[];
};

export type ExploreFieldOutOfSyncError =
  | {
      type: "missing";
    }
  | {
      type: "dialect-incompatible-agg";
      aggregation: HqlAggregationFunction;
    }
  | {
      type: "incompatible-type";
      expectedType: FilledDynamicValueTableColumnType;
      currentType: FilledDynamicValueTableColumnType;
    };

export const flattenFieldGroups = (
  fieldGroups: SemanticAwareFieldGroup[],
): SemanticAwareColumn[] =>
  fieldGroups.flatMap((fg) => [
    ...fg.columns,
    ...fg.measures,
    ...flattenFieldGroups(fg.children),
  ]);

export const getTypeCategory = (
  dataType: FilledDynamicValueTableColumnType | CalciteType,
) => {
  switch (dataType) {
    case "NUMBER":
    case "FLOAT":
    case "DOUBLE":
    case "BIGINT":
    case "INTEGER":
    case "DECIMAL":
      return "numeric";
    case "STRING":
    case "VARCHAR":
      return "string";
    case "BOOLEAN":
      return "boolean";
    case "DATE":
    case "TIME":
    case "DATETIME":
    case "DATETIMETZ":
    case "TIMESTAMP":
    case "TIMESTAMPTZ":
      return "datetime";
    case "UNKNOWN":
      return "unknown";
    default:
      assertNever(dataType, dataType);
  }
};

const dimensionChannels = new Set<ExploreDropSection["type"]>([
  "base-axis",
  "color",
  "opacity",
  "h-facet",
  "v-facet",
  "tooltip",
  "row",
  "column",
  "groupby",
]);
const stringLike = ["string", "boolean"];

export const areTypesCompatibleForUsage = ({
  channel,
  currentType,
  expectedType,
  usage,
}: {
  expectedType: FilledDynamicValueTableColumnType | CalciteType;
  currentType: FilledDynamicValueTableColumnType | CalciteType;
  channel?: ExploreDropSection["type"];
  aggregation?: HqlAggregationFunction;
  usage?: "viz" | "filter" | "details";
}): boolean => {
  const expectedCategory = getTypeCategory(expectedType);
  const currentCategory = getTypeCategory(currentType);
  if (expectedCategory === currentCategory) {
    return true;
  }
  if (expectedCategory === "unknown" || currentCategory === "unknown") {
    return true;
  }

  const isDimensionUsage =
    usage === "filter" ||
    usage === "details" ||
    (channel != null && dimensionChannels.has(channel));

  if (isDimensionUsage) {
    if (
      stringLike.includes(expectedCategory) &&
      stringLike.includes(currentCategory)
    ) {
      return true;
    }
  }

  return false;
};

export const isExploreTableExpanded = (spec: ExploreSpec): boolean => {
  const noDetailFieldsSelected =
    spec.details.fields.length === 0 &&
    !(spec.fields.length === 0 && spec.details.showAllBaseTableDetailFields);

  // If we're in state where the detail fields have been expanded but there are
  // no fields... then we want to treat this as a collapsed table state and show
  // the "expand" button (which will add all detail fields when clicked if no detail fields)
  return spec.details.enabled && !noDetailFieldsSelected;
};

interface UseExploreFieldsOutOfSyncParams {
  calcs: CalcsDefinition;
  cellId: CellId;
  dialect: SqlDialect;
  fieldGroups: SemanticAwareFieldGroup[];
  filters: readonly ColumnFilter[];
  spec: ExploreSpec;
}

export const useExploreFieldsOutOfSync = ({
  calcs,
  cellId,
  dialect,
  fieldGroups,
  filters,
  spec,
}: UseExploreFieldsOutOfSyncParams): ExploreFieldOutOfSync[] => {
  // Don't run logic on every explore spec update
  const outOfSyncFields = useComputedValue(() => {
    return getExploreFieldsOutOfSync(
      cellId,
      spec,
      dialect,
      calcs,
      filters,
      fieldGroups,
    );
  }, [
    calcs,
    fieldGroups,
    filters.length,
    spec.fields.length,
    spec.details.fields?.length,
    spec.details.enabled,
  ]);

  // Figure out whether to even report issues, based on loading state / magic
  const getLatestMagicEventId = useCellGetter({
    selector: (cell) => {
      return cell.latestMagicEventId;
    },
  });
  const isUsingMagic = !!getLatestMagicEventId(cellId);
  const hasColumnInfoLoaded = fieldGroups.some((fieldGroup, idx) => {
    // Calcs only supported on the base table
    const numNonCalcColumns =
      idx === 0
        ? fieldGroup.columns.length - calcs.calcColumns.length
        : fieldGroup.columns.length;
    return numNonCalcColumns > 0;
  });
  const haveJoinedTablesLoaded = spec.tables
    ? spec.tables.length === fieldGroups.length
    : true;
  if (!hasColumnInfoLoaded || !haveJoinedTablesLoaded || isUsingMagic) {
    // Don't flag any issues if we think the explore is still loading
    // e.g. after Ask Magic, the spec exists but the fields aren't available
    //
    // isUsingMagic - sometimes magic populates the spec at the same time it switches
    // the input table; so it'll be out of sync for the 2 seconds it takes the new table's
    // column info to load
    return [];
  }

  return outOfSyncFields;
};

const getExploreFieldsOutOfSync = (
  cellId: CellId,
  spec: ExploreSpec,
  dialect: SqlDialect,
  calcs: CalcsDefinition,
  filters: readonly ColumnFilter[],
  fieldGroups: SemanticAwareFieldGroup[],
): ExploreFieldOutOfSync[] => {
  const { errors: calcErrors } = validateCalcDefinition(calcs, {
    disableHqlNextOnlyFunctions: false,
  });
  const fieldsWithCalcErrors = new Set(
    Array.from(calcErrors.entries()).flatMap(([calcName, errors]) =>
      errors.some(isCalcErrorFatal) ? [calcName] : [],
    ),
  );
  // The left sidebar indicates which calcs have errors
  // When a calc has errors, its column type becomes "unknown" - often out of sync.
  // No need to also flag it here
  const specFieldsMinusKnownCalcErrors = spec.fields.filter(
    (f) => !fieldsWithCalcErrors.has(f.value),
  );

  const flatFields = flattenFieldGroups(fieldGroups);
  const fieldLookup = new Map(
    flatFields.map((f) => [generateColumnIdForField(f), f]),
  );
  // The same field might pop up in a few places
  // It might not have an error; that's for the loop after this one to decide
  const uniqFieldsToPartialResult: Record<
    string,
    Omit<ExploreFieldOutOfSync, "error"> & {
      dataType: CalciteType;
      aggregation?: HqlAggregationFunction;
    }
  > = {};
  const fieldsToCheck: (
    | ExploreField
    | ExploreDetailField
    | (Omit<ExploreDetailField, "fieldType"> & {
        location: "filter";
      })
  )[] = [
    ...specFieldsMinusKnownCalcErrors,
    ...(spec.details.enabled ? (spec.details.fields ?? []) : []),
    ...filters.map(
      (filter) =>
        ({
          value: filter.column,
          queryPath: filter.queryPath ?? [],
          // A little silly to convert back and forth
          dataType: columnTypeToCalciteType(filter.columnType ?? "UNKNOWN"),
          location: "filter",
        }) as const,
    ),
  ];

  const referencedColumnToCalcColumn = getReferencedColumnToCalcColumn(
    calcs.calcColumns,
  );
  fieldsToCheck.forEach((field) => {
    const key = generateColumnIdForField(field);
    if (uniqFieldsToPartialResult[key] == null) {
      uniqFieldsToPartialResult[key] = {
        fieldId: key,
        channel: "channel" in field ? field.channel : undefined,
        usedInDetails: !("channel" in field),
        usedInFilters: "location" in field && field.location === "filter",

        calcColumnNames: referencedColumnToCalcColumn[key] ?? [],
        dataType: field.dataType,
        aggregation: "aggregation" in field ? field.aggregation : undefined,
      };
    } else {
      uniqFieldsToPartialResult[key].channel ||=
        "channel" in field ? field.channel : undefined;
      uniqFieldsToPartialResult[key].usedInDetails ||= "visible" in field;
      uniqFieldsToPartialResult[key].usedInFilters ||= "location" in field;
      uniqFieldsToPartialResult[key].aggregation ??=
        "aggregation" in field ? field.aggregation : undefined;
    }
  });

  const outOfSyncFields: ExploreFieldOutOfSync[] = [];
  for (const [fieldId, partialResult] of typedObjectEntries(
    uniqFieldsToPartialResult,
  )) {
    // If this field is only present in the details table, we currently choose
    // not to surface out-of-sync errors. The user experience for details
    // mismatches is too noisy and doesn't impact the actual viz or filters.
    if (
      partialResult.usedInDetails &&
      partialResult.channel == null &&
      !partialResult.usedInFilters
    ) {
      continue;
    }
    const sameFieldFromSchema = fieldLookup.get(fieldId);
    if (fieldId === CHART_FOLD_KEYS) {
      continue;
    }
    if (sameFieldFromSchema == null) {
      outOfSyncFields.push({
        ...partialResult,
        error: { type: "missing" } as const,
      });
      continue;
    }
    if (
      !areTypesCompatibleForUsage({
        expectedType: partialResult.dataType,
        currentType: sameFieldFromSchema.columnType,
        channel: spec.fields.find(
          (f) => generateColumnIdForField(f) === fieldId,
        )?.channel as ExploreDropSection["type"] | undefined,
        usage: partialResult.usedInFilters
          ? "filter"
          : partialResult.usedInDetails
            ? "details"
            : "viz",
      })
    ) {
      outOfSyncFields.push({
        ...partialResult,
        error: {
          type: "incompatible-type",
          expectedType: calciteTypeToColumnType(partialResult.dataType),
          currentType: sameFieldFromSchema.columnType,
        } as const,
      });
    }
    if (
      partialResult.aggregation != null &&
      !sqlGenSupportsAggForDialect(partialResult.aggregation, dialect)
    ) {
      outOfSyncFields.push({
        ...partialResult,
        error: {
          type: "dialect-incompatible-agg",
          aggregation: partialResult.aggregation,
        } as const,
      });
    }
  }
  return outOfSyncFields;
};

export const getReferencedColumnToCalcColumn = (
  calcColumns: readonly CalcColumnDefinition[],
): Record<string, DisplayTableColumnId[]> => {
  const allASTs = calcColumns.map((c) => parseCalcExpression(c.expression));
  const referencedColumnToCalcColumn: Record<string, DisplayTableColumnId[]> =
    {};
  calcColumns.forEach((calcColumn, i) => {
    const ast = allASTs[i];
    const referencedColumns = getAllNodesOfType(ast, "column").map(
      ({ name }) => name,
    );
    referencedColumns.forEach((column) => {
      if (!referencedColumnToCalcColumn[column]) {
        referencedColumnToCalcColumn[column] = [];
      }
      referencedColumnToCalcColumn[column].push(calcColumn.name);
    });
  });
  return referencedColumnToCalcColumn;
};

export const getExploreFilterDropAction = (
  inputField: ExploreField | ExploreInputField | SemanticAwareColumn,
  fields: ExploreField[],
) => {
  const fieldInViz = fields.some(
    (f) => generateColumnIdForField(f) === generateColumnIdForField(inputField),
  );
  return getExploreFilterDropActionWithoutFields(inputField, fieldInViz);
};

export const getExploreFilterDropActionWithoutFields = (
  inputField: ExploreField | ExploreInputField | SemanticAwareColumn,
  fieldInViz: boolean,
) => {
  const isMeasure =
    inputField.fieldType === "MEASURE" ||
    ("aggregation" in inputField && inputField.aggregation != null);
  if (isMeasure) {
    if (inputField.queryPath.length > 0) {
      return {
        type: "not-allowed" as const,
        reason: "Can only filter by base table measures for now",
      };
    }
    if (fieldInViz) {
      return { type: "add" as const };
    }
    return {
      type: "not-allowed" as const,
      reason: `Add measure to chart or table before filtering`,
    };
  }
  return { type: "add" as const };
};

export type DisplayTableConfigForFilters = Pick<
  DisplayTableConfigFragment,
  "filters" | "id"
>;

/**
 * Remove a specific field by ID and clean up associated display table filters
 * for measures and aggregated dimensions.
 */
export const removeFieldWithFilterAndLumpCleanup = ({
  cellId,
  displayTableConfig,
  fieldId,
  fields,
}: {
  cellId: CellId;
  fieldId: string;
  fields: ExploreField[];
  displayTableConfig: DisplayTableConfigForFilters | null;
}): {
  actions: ExploreSpecUpdateActions[];
  additionalActions?: HexVersionAtomicOperation[];
} => {
  const field = fields.find((f) => f.id === fieldId);
  if (!field) {
    return { actions: [] };
  }

  const filters = displayTableConfig?.filters ?? [];

  // Remove filters that reference this field.
  // – For MEASUREs we drop *all* filters on that column (no aggregation present).
  // – For aggregated DIMENSIONs we only drop filters that themselves have an aggregation set.
  // - For lump filters, we drop the filter if the lump was ordered by the removed field
  const filtersToKeep = filters.filter((filter) => {
    if (
      zGuard(LumpColumnPredicate, filter.predicate) &&
      filter.lumpOrderBy === fieldId
    ) {
      return false;
    }

    if (filter.column !== field.value) {
      return true;
    }

    if (field.fieldType === "MEASURE") {
      // Always remove measure filter when the measure is removed from the viz.
      return false;
    }

    // For dimensions, keep the filter unless both the field and the filter are aggregated.
    return !(field.aggregation != null && filter.aggregation);
  });

  const fieldLumpUpdateActions = fields
    .map((f) =>
      f.lump?.orderBy === fieldId
        ? exploreSpecActions.setFieldProperty({
            fieldId: f.id,
            updateData: {
              lump: undefined,
            },
          })
        : undefined,
    )
    .filter(notNullish);

  const actions = [
    exploreSpecActions.removeField({ fieldId: fieldId as ExploreFieldId }),
    ...fieldLumpUpdateActions,
  ];

  // When removing a 2nd cross-axis field, remove any CHART_FOLD_KEYS fields for that series
  if (field.channel === "cross-axis") {
    const crossAxisFields = fields.filter(
      (f) => f.channel === "cross-axis" && f.seriesId === field.seriesId,
    );

    if (crossAxisFields.length === 2) {
      actions.push(
        ...fields
          .filter(
            (f) => f.seriesId === field.seriesId && f.value === CHART_FOLD_KEYS,
          )
          .map((f) => exploreSpecActions.removeField({ fieldId: f.id })),
      );
    }
  }

  const additionalActions =
    !!displayTableConfig && filtersToKeep.length !== filters.length
      ? [
          UPDATE_DISPLAY_TABLE_CONFIG.create({
            suppressAutoRun: true,
            cellId,
            displayTableConfigId: displayTableConfig.id,
            key: "filters",
            value: filtersToKeep,
          }),
        ]
      : undefined;

  return { actions, additionalActions };
};

export const replaceFieldWithFilterCleanup = (
  cellId: CellId,
  fieldToReplaceId: string,
  newField: ExploreInputField,
  fields: ExploreField[],
  displayTableConfig: DisplayTableConfigForFilters | null,
): {
  actions: ExploreSpecUpdateActions[];
  additionalActions?: HexVersionAtomicOperation[];
} => {
  const fieldToReplace = fields.find((f) => f.id === fieldToReplaceId);
  if (!fieldToReplace) {
    // If the field cannot be found, just fall back to the normal action
    return {
      actions: [
        exploreSpecActions.replaceField({
          fieldToReplaceId: fieldToReplaceId as ExploreFieldId,
          newField,
        }),
      ],
    };
  }

  const filters = displayTableConfig?.filters ?? [];

  // Remove any filter that targets this column and is aggregated. Aggregated filters
  // correspond to measures / aggregated dimensions which may no longer be present
  // after the replacement.
  const filtersToKeep = filters.filter(
    (f) => f.column !== fieldToReplace.value || !f.aggregation,
  );

  const actions: ExploreSpecUpdateActions[] = [
    exploreSpecActions.replaceField({
      fieldToReplaceId: fieldToReplaceId as ExploreFieldId,
      newField,
    }),
  ];

  const additionalActions =
    !!displayTableConfig && filtersToKeep.length !== filters.length
      ? [
          UPDATE_DISPLAY_TABLE_CONFIG.create({
            suppressAutoRun: true,
            cellId,
            displayTableConfigId: displayTableConfig.id,
            key: "filters",
            value: filtersToKeep,
          }),
        ]
      : undefined;

  return { actions, additionalActions };
};

/**
 * Clear all explore fields and clean up any filters that reference measures or
 * aggregated dimensions (identified by the presence of `aggregation`).
 */
export const clearFieldsWithFilterCleanup = (
  cellId: CellId,
  displayTableConfig: DisplayTableConfigForFilters | null,
): {
  actions: ExploreSpecUpdateActions[];
  additionalActions?: HexVersionAtomicOperation[];
} => {
  const filters = displayTableConfig?.filters ?? [];
  const filtersToKeep = filters.filter((f) => !f.aggregation);

  const actions: ExploreSpecUpdateActions[] = [
    exploreSpecActions.clearFields(),
  ];

  const additionalActions =
    !!displayTableConfig && filtersToKeep.length !== filters.length
      ? [
          UPDATE_DISPLAY_TABLE_CONFIG.create({
            suppressAutoRun: true,
            cellId,
            displayTableConfigId: displayTableConfig.id,
            key: "filters",
            value: filtersToKeep,
          }),
        ]
      : undefined;

  return { actions, additionalActions };
};

/**
 * Returns a query path for the field group if and only if all items
 * in the field group (at this level) have the same query path, which may not be
 * true for field groups created by semantic views, for example.
 */
export const getFieldGroupUniformQueryPath = (
  fieldGroup: SemanticAwareFieldGroup,
): SemanticDatasetName[] | null => {
  if (fieldGroup.columns.length === 0 && fieldGroup.measures.length === 0) {
    return null;
  }
  const firstQueryPath =
    fieldGroup.columns[0]?.queryPath ?? fieldGroup.measures[0]?.queryPath;
  for (const column of fieldGroup.columns) {
    if (column.queryPath !== firstQueryPath) {
      return null;
    }
  }
  for (const measure of fieldGroup.measures) {
    if (measure.queryPath !== firstQueryPath) {
      return null;
    }
  }
  return firstQueryPath;
};
